import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { PROGRAM_MAP } from '../../utils/programs';

interface OfficeSuiteProps {
  onOpenApp?: (appId: string, title: string) => void;
}

export const OfficeSuite: React.FC<OfficeSuiteProps> = ({ onOpenApp }) => {
  const [showWhatsNew, setShowWhatsNew] = useState(false);

  const officeApps = [
    {
      id: 'maple-word',
      name: 'MapleWord',
      description: 'Advanced Word Processor',
      icon: PROGRAM_MAP['maple-word']?.icon,
    },
    {
      id: 'maple-sheet',
      name: 'MapleSheet',
      description: 'Powerful Spreadsheet Application',
      icon: PROGRAM_MAP['maple-sheet']?.icon,
    },
    {
      id: 'maple-slide',
      name: 'MapleSlide',
      description: 'Professional Presentation Software',
      icon: PROGRAM_MAP['maple-slide']?.icon,
    },
    {
      id: 'maple-calc',
      name: 'MapleCalc',
      description: 'Scientific Calculator',
      icon: PROGRAM_MAP['maple-calc']?.icon,
    },
    {
      id: 'maple-money',
      name: 'MapleMoney',
      description: 'Personal Finance Manager',
      icon: PROGRAM_MAP['maple-money']?.icon,
    },
    {
      id: 'maple-stats',
      name: 'MapleStats',
      description: 'Advanced Statistical Analysis',
      icon: PROGRAM_MAP['maple-stats']?.icon,
    },
  ];

  const handleAppLaunch = (appId: string, appName: string) => {
    if (onOpenApp) {
      onOpenApp(appId, appName);
    }
  };

  return (
    <>
      <div className="flex h-full min-h-0 flex-col bg-background">
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold text-foreground">MapleOS Office Suite</h1>
            <p className="text-lg text-muted-foreground">Professional productivity applications for your desktop</p>
          </div>

          <div className="mb-6 flex items-center border-b border-border/70 pb-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-sm font-medium"
              onClick={() => setShowWhatsNew(true)}
            >
              What&apos;s New
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {officeApps.map((app) => {
              const Icon = app.icon;

              return (
                <Card
                  key={app.id}
                  className="group cursor-pointer border-border/70 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
                  onClick={() => handleAppLaunch(app.id, app.name)}
                >
                  <CardHeader className="items-center py-10 text-center">
                    <div className="mb-4 flex justify-center">
                      {Icon ? <Icon size={72} className="shrink-0 transition-transform duration-200 group-hover:scale-110" /> : null}
                    </div>
                    <div>
                      <CardTitle className="text-xl">{app.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{app.description}</p>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={showWhatsNew} onOpenChange={setShowWhatsNew}>
        <DialogContent className="z-[9999] sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>What&apos;s New in Office Suite</DialogTitle>
            <DialogDescription>
              Recent updates across MapleWord, MapleSlide, and MapleSheet.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 text-sm md:grid-cols-2">
            <div>
              <h3 className="mb-2 font-medium">MapleWord Updates</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Enhanced rich text editor with advanced formatting</li>
                <li>• Real-time collaboration features</li>
                <li>• Improved document templates</li>
              </ul>
            </div>

            <div>
              <h3 className="mb-2 font-medium">MapleSlide Updates</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Professional presentation editor with Reveal.js</li>
                <li>• Multiple slide layouts and themes</li>
                <li>• Full-screen presentation mode</li>
                <li>• HTML export for web sharing</li>
              </ul>
            </div>

            <div>
              <h3 className="mb-2 font-medium">MapleSheet Updates</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Advanced charting capabilities</li>
                <li>• New formula functions</li>
                <li>• Enhanced data import/export</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
